import { Dialog, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { TbDeviceFloppy } from "react-icons/tb";
import { type Borrower, BorrowerNewBodySchema } from "shared";
import { Api } from "@/api.config";
import { Button } from "@/components/Button";
import { NumberInput } from "@/components/form/NumberInput";
import { TextField } from "@/components/form/TextField";
import { QueryKeys } from "@/LinkGen";

interface AddBorrowerDialogProps {
  onConfirm?: (id: number) => unknown;
  borrower?: Partial<Borrower>;
}

export const EditBorrowerDialog = (props: AddBorrowerDialogProps) => {
  const { borrower, onConfirm } = props;
  const dialog = useDialog();

  const { handleSubmit, formState, control } = useForm({
    resolver: zodResolver(BorrowerNewBodySchema),
    defaultValues: {
      firstName: borrower?.firstName ?? "",
      lastName: borrower?.lastName ?? "",
      email: borrower?.email ?? "",
      creditScore: borrower?.creditScore ?? "",
    },
  });
  const queryClient = useQueryClient();

  const onSubmit = handleSubmit(async (values) => {
    if (borrower?.ID) {
      await Api.BorrowerEdit(borrower.ID, values);
      await queryClient.refetchQueries({
        queryKey: QueryKeys.Borrower(borrower.ID),
      });
    } else {
      const created = await Api.BorrowerNew(values).then((res) => res.data);
      queryClient.setQueryData(QueryKeys.Borrower(created.ID), () => created);
      onConfirm?.(created.ID);
    }
    dialog.close();
  });

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>Create a new borrower</Dialog.Header>
      <Dialog.Body>
        <TextField
          label="First name"
          placeholder="John"
          autoFocus
          control={control}
          name="firstName"
          required
        />
        <TextField
          label="Last name"
          placeholder="Smith"
          control={control}
          name="lastName"
          required
        />
        <TextField
          label="Email"
          placeholder="john.smith@example.com"
          control={control}
          name="email"
          required
        />
        <NumberInput
          label="Credit Score"
          placeholder="680"
          control={control}
          name="creditScore"
        />
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.Footer>
    </form>
  );
};
